function isServerSide() {
  return typeof window === 'undefined'
}

function getAPIBaseUrl() {
  if (isServerSide()) {
    return process.env.NEXT_PUBLIC_API_BASE_URL
  }
  return '/api'
}

export type QueryParams = Record<string, string | number | boolean | string[] | null | undefined>
export type ApiClientRequestConfig = { headers?: Record<string, string>; params?: QueryParams }
export type ApiClientResponse<T> = { data: T }
export type ApiClientOptions = { headers?: Record<string, string> }
// Mirrors axios's implicit `any` response type default, so call sites that don't pass an
// explicit generic (e.g. `this.client.get(path)`, `error.response?.data?.message`) keep
// behaving the same as before.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DefaultResponseType = any

export class HttpRequestError extends Error {
  constructor(
    public readonly status: number,
    public readonly response: { status: number; data: DefaultResponseType }
  ) {
    super(`Request failed with status ${String(status)}`)
    this.name = 'HttpRequestError'
  }
}

async function parseBody(res: Response): Promise<unknown> {
  const text = await res.text()
  // Matches axios's transformResponse: an empty body skips JSON.parse and is
  // returned as-is (`''`), rather than `undefined` — so `data?.message`-style
  // access on error responses with no body doesn't throw.
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

export class ApiClient {
  constructor(
    private baseURL: string,
    private defaultOptions: ApiClientOptions & { credentials?: RequestCredentials } = {}
  ) {}

  // Joins baseURL + path the same way axios's `combineURLs` does (plain string
  // concatenation), rather than the URL constructor's resolution rules — those differ
  // because our client-side baseURL is the relative path `/api`, not an absolute URL,
  // and passing a relative `base` to `new URL()` throws.
  private buildUrl(path: string, params?: QueryParams) {
    const base = this.baseURL.replace(/\/+$/, '')
    const cleanPath = path.replace(/^\/+/, '')
    const url = `${base}/${cleanPath}`

    const searchParams = new URLSearchParams()
    for (const [key, value] of Object.entries(params ?? {})) {
      if (value === undefined || value === null) continue
      if (Array.isArray(value)) {
        value.forEach((item) => {
          searchParams.append(key, item)
        })
      } else {
        searchParams.set(key, String(value))
      }
    }

    const queryString = searchParams.toString()
    return queryString ? `${url}?${queryString}` : url
  }

  private async send<T>(
    method: string,
    path: string,
    body?: unknown,
    config: ApiClientRequestConfig = {}
  ): Promise<ApiClientResponse<T>> {
    const isFormData = body instanceof FormData
    const headers = {
      ...(body !== undefined && !isFormData ? { 'Content-Type': 'application/json' } : {}),
      ...this.defaultOptions.headers,
      ...config.headers,
    }

    const res = await fetch(this.buildUrl(path, config.params), {
      method,
      headers,
      credentials: this.defaultOptions.credentials,
      body: body === undefined || isFormData ? body : JSON.stringify(body),
    })

    const data = await parseBody(res)
    if (!res.ok) {
      throw new HttpRequestError(res.status, { status: res.status, data })
    }
    return { data: data as T }
  }

  get<T = DefaultResponseType>(path: string, config?: ApiClientRequestConfig) {
    return this.send<T>('GET', path, undefined, config)
  }

  post<T = DefaultResponseType>(path: string, body?: unknown, config?: ApiClientRequestConfig) {
    return this.send<T>('POST', path, body, config)
  }

  put<T = DefaultResponseType>(path: string, body?: unknown, config?: ApiClientRequestConfig) {
    return this.send<T>('PUT', path, body, config)
  }

  patch<T = DefaultResponseType>(path: string, body?: unknown, config?: ApiClientRequestConfig) {
    return this.send<T>('PATCH', path, body, config)
  }

  delete<T = DefaultResponseType>(path: string, config?: ApiClientRequestConfig) {
    return this.send<T>('DELETE', path, undefined, config)
  }
}

export const initApiClient = (options: ApiClientOptions = {}) => {
  return new ApiClient(getAPIBaseUrl() ?? '', { credentials: 'include', ...options })
}
